import { useState } from "react";

interface AuthDialogMessage {
  error?: string;
  success?: boolean;
  authCache?: Record<string, string>;
}

const parseAuthDialogMessage = (message: string): AuthDialogMessage => {
  const parsed = JSON.parse(message) as AuthDialogMessage;
  return parsed;
};

export const useAuthDialog = () => {
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const openLoginDialog = (shouldReload = true) => {
    return new Promise((resolve, reject) => {
      setIsAuthenticating(true);
      const loginUrl = `${window.location.origin}/auth-login.html`;

      Office.context.ui.displayDialogAsync(loginUrl, { height: 60, width: 30 }, (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          console.error("Failed to open dialog:", asyncResult.error.message);
          setIsAuthenticating(false);
          reject(new Error(asyncResult.error.message));
          return;
        }

        const dialog = asyncResult.value;

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, async (arg) => {
          try {
            if (!("message" in arg)) {
              console.error("Authentication error:", arg.error);
              dialog.close();
              setIsAuthenticating(false);
              reject(new Error(`Authentication dialog failed with code ${arg.error}`));
              return;
            }

            const data = parseAuthDialogMessage(arg.message);

            if (data.error) {
              console.error("Authentication error:", data.error);
              alert("Authentication failed: " + data.error);
              dialog.close();
              setIsAuthenticating(false);
              reject(new Error(data.error));
            } else if (data.success) {
              // Restore Auth0 cache entries into the taskpane's localStorage.
              // In Word Online, the dialog and taskpane have separate
              // partitioned storage, so tokens written by the dialog are not
              // visible here. This bridges the gap. On Desktop Word this is a
              // harmless no-op (same storage, same data).
              if (data.authCache) {
                Object.entries(data.authCache).forEach(([key, value]) => {
                  localStorage.setItem(key, value);
                });
              }

              dialog.close();
              setIsAuthenticating(false);

              if (shouldReload) {
                // Reload to trigger authentication check
                window.location.reload();
              } else {
                // Resolve the promise so caller can handle next steps
                resolve({ success: true });
              }
            }
          } catch (error) {
            console.error("Error processing auth message:", error);
            dialog.close();
            setIsAuthenticating(false);
            reject(error);
          }
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, (_arg) => {
          dialog.close();
          setIsAuthenticating(false);
          reject(new Error("Dialog was closed by user"));
        });
      });
    });
  };

  return {
    isAuthenticating,
    openLoginDialog,
  };
};
